import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";
import { cameraAt, cameraTransformCentered, type CameraKey, type CameraTrack } from "./camera";
import { settle, settleScale } from "./anim";

/**
 * Anivaze v4 — FLEXIBLE FLOW / ARCHITECTURE actor (PLATFORM-ADDITIONS §6).
 *
 * Renders an arbitrary hierarchical flow — the "EU Customer → SaaS → [Tenant A |
 * Tenant B | Tenant C] → AI Layer → Bedrock → Claude" shape the stakeholder
 * showed — as clean vector cards with elbow connectors and fan-out levels.
 *
 * Encoding (planner-friendly): items is an ordered list of LEVELS, top-down.
 *  - a plain item  ("Your SaaS Platform")        → one linear step
 *  - a piped item  ("Tenant A | Tenant B | ...")  → one PARALLEL fan-out level
 *
 * Length-adaptive: when the flow is taller than its visual box it lays out in a
 * tall WORLD and the actor scrolls internally (camera.ts) — opens on an overview
 * of the whole graph, then eases DOWN section-by-section as the beat plays, while
 * the beat's headline + captions stay put. Pure function of `t`; the nodes build
 * on progressively. No global camera needed — the scroll is local to the box.
 */

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const smooth = (x: number) => { const c = clamp01(x); return c * c * (3 - 2 * c); };

interface VProps { items: string[]; box: Box; theme: Theme; unit: number; t: number }

export interface FlowNode { label: string; level: number; box: Box }
export interface ArchLayout {
  world: Box;
  levels: { y: number; nodes: FlowNode[] }[];
  nodeW: number;
  nodeH: number;
  rowGap: number;
}

/** Split items into levels; "A | B | C" (pipe or slash) is one parallel level. */
export function parseFlowLevels(items: string[]): string[][] {
  return (items ?? [])
    .map((s) => String(s ?? "").trim())
    .filter(Boolean)
    .map((s) => s.split(/\s*[|/]\s*/).map((x) => x.trim()).filter(Boolean))
    .filter((labels) => labels.length > 0);
}

/** Lay the levels out top-down in a world sized to the content (width = box.w). */
export function layoutArchFlow(items: string[], box: Box, u: number): ArchLayout {
  const levels = parseFlowLevels(items);
  const nodeH = 7 * u;
  const rowGap = 12 * u;
  const maxCols = Math.max(1, ...levels.map((l) => l.length));
  const worldW = box.w;
  const nodeW = Math.min(worldW * 0.62, Math.max(16 * u, worldW / (maxCols + 0.5)));
  const topPad = nodeH;
  const out: { y: number; nodes: FlowNode[] }[] = [];
  levels.forEach((labels, i) => {
    const y = box.y + topPad + i * rowGap;
    const k = labels.length;
    const spread = k === 1 ? 0 : Math.min(worldW - nodeW, (k - 1) * (nodeW + 3 * u));
    const startX = box.cx - spread / 2;
    const nodes: FlowNode[] = labels.map((label, j) => {
      const cx = k === 1 ? box.cx : startX + (spread / (k - 1)) * j;
      return { label, level: i, box: { x: cx - nodeW / 2, y: y - nodeH / 2, w: nodeW, h: nodeH, cx, cy: y } };
    });
    out.push({ y, nodes });
  });
  const worldH = topPad * 2 + Math.max(0, levels.length - 1) * rowGap + nodeH;
  const world: Box = { x: box.x, y: box.y, w: worldW, h: Math.max(box.h, worldH), cx: box.cx, cy: box.y + worldH / 2 };
  return { world, levels: out, nodeW, nodeH, rowGap };
}

const STEP = 0.75;        // seconds between level reveals
const INTRO_HOLD = 0.9;   // overview hold before scrolling

/** Build the internal scroll: overview → ease down level-by-level. */
export function buildFlowCamera(layout: ArchLayout, box: Box): CameraTrack {
  const { world, levels, rowGap, nodeH } = layout;
  const overviewZoom = Math.min(box.w / (world.w * 1.04), box.h / (world.h * 1.04));
  const fitsAll = world.h <= box.h * 1.04 || levels.length <= 1;
  if (fitsAll) return [{ t: 0, cx: world.cx, cy: world.cy, zoom: overviewZoom }];

  const readVisibleH = Math.min(world.h, 3.1 * rowGap + nodeH); // ~3 rows in view
  const readZoom = box.h / readVisibleH;
  const halfH = box.h / 2 / readZoom;
  const clampCy = (cy: number) => Math.max(world.y + halfH, Math.min(world.y + world.h - halfH, cy));

  const keys: CameraKey[] = [
    { t: 0, cx: world.cx, cy: world.cy, zoom: overviewZoom },
    { t: INTRO_HOLD, cx: world.cx, cy: world.cy, zoom: overviewZoom },
  ];
  levels.forEach((lv, i) => {
    keys.push({ t: INTRO_HOLD + 0.5 + i * STEP, cx: world.cx, cy: clampCy(lv.y), zoom: readZoom });
  });
  return keys;
}

function FlowCard({ node, theme, u, appear, scaleIn }: { node: FlowNode; theme: Theme; u: number; appear: number; scaleIn: number }) {
  if (appear <= 0.001) return null;
  const P = theme.palette;
  const b = node.box;
  const s = scaleIn;
  const fontSize = Math.min(u * 2.0, (b.w * 0.86) / Math.max(6, node.label.length * 0.52));
  return (
    <g opacity={appear} transform={`translate(${b.cx} ${b.cy}) scale(${s.toFixed(3)}) translate(${-b.cx} ${-b.cy})`}>
      <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={theme.radius * 0.35}
        fill={P.surface2} stroke={P.primary} strokeWidth={theme.strokeWidth * 0.85} />
      <text x={b.cx} y={b.cy} textAnchor="middle" dominantBaseline="central"
        fontFamily={theme.title.family} fontWeight={700} fontSize={fontSize} fill={P.ink}>
        {node.label}
      </text>
    </g>
  );
}

/** Elbow connectors between consecutive levels (fan-out / converge / 1:1). */
function edges(layout: ArchLayout, theme: Theme, t: number): React.ReactNode[] {
  const P = theme.palette;
  const els: React.ReactNode[] = [];
  for (let i = 0; i < layout.levels.length - 1; i++) {
    const a = layout.levels[i].nodes;
    const b = layout.levels[i + 1].nodes;
    const appear = smooth(clamp01((t - (0.3 + (i + 1) * STEP)) / 0.4));
    if (appear <= 0.001) continue;
    const pairs: [FlowNode, FlowNode][] = [];
    if (a.length === 1) b.forEach((cn) => pairs.push([a[0], cn]));
    else if (b.length === 1) a.forEach((pn) => pairs.push([pn, b[0]]));
    else { const m = Math.min(a.length, b.length); for (let k = 0; k < m; k++) pairs.push([a[k], b[k]]); }
    pairs.forEach(([pn, cn], k) => {
      const px = pn.box.cx, pby = pn.box.y + pn.box.h;
      const cxr = cn.box.cx, cty = cn.box.y;
      const midY = (pby + cty) / 2;
      const d = `M ${px} ${pby} L ${px} ${midY} L ${cxr} ${midY} L ${cxr} ${cty}`;
      els.push(
        <path key={`e-${i}-${k}`} d={d} fill="none" stroke={P.muted}
          strokeWidth={theme.strokeWidth * 0.7} opacity={0.55 * appear}
          strokeLinecap="round" strokeLinejoin="round" />,
      );
    });
  }
  return els;
}

export function ArchFlowActor({ items, box, theme, unit, t }: VProps) {
  const u = unit;
  const layout = layoutArchFlow(items, box, u);
  if (layout.levels.length === 0) return null;
  const cam = cameraAt(buildFlowCamera(layout, box), t);
  const transform = cameraTransformCentered(cam, box.cx, box.cy);
  const seed = `${layout.levels.length}-${(items[0] ?? "").replace(/[^a-z0-9]/gi, "").slice(0, 12)}`;
  const clipId = `af-clip-${seed}`;
  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect x={box.x} y={box.y} width={box.w} height={box.h} rx={theme.radius * 0.3} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <g transform={transform}>
          {edges(layout, theme, t)}
          {layout.levels.map((lv, i) =>
            lv.nodes.map((n, j) => (
              // Cards ARRIVE with a spring pop (settle + overshoot, anim.ts) and a
              // small per-node overlap inside a level — never a simultaneous fade.
              <FlowCard key={`n-${i}-${j}`} node={n} theme={theme} u={u}
                appear={settle(t - (0.3 + i * STEP + j * 0.06), 0.45, 0.3)}
                scaleIn={settleScale(t - (0.3 + i * STEP + j * 0.06), 0.45, 0.3)} />
            )),
          )}
        </g>
      </g>
    </g>
  );
}
